import { useEffect, useState, type FormEvent } from "react";
import api from "../../services/api";
import { useToast } from "../../contexts/toast/toast-context";
import { getApiErrorMessage } from "../../utils";
import { LoadingButton } from "../button/loading-button";
import { PartySelect } from "../../pages/painel/protocols/party-select";
import {
  priorityLabels,
  type ProtocolPriority,
} from "../../types/protocol";

interface ConversationProtocolModalProps {
  isOpen: boolean;
  onClose: () => void;
  conversationId: string;
  onCreated?: () => void;
}

export function ConversationProtocolModal({
  isOpen,
  onClose,
  conversationId,
  onCreated,
}: ConversationProtocolModalProps) {
  const { error, success } = useToast();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<ProtocolPriority>("NORMAL");
  const [assignedToSystemUserId, setAssignedToSystemUserId] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) return;
    setTitle("");
    setDescription("");
    setPriority("NORMAL");
    setAssignedToSystemUserId("");
  }, [isOpen]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (isSaving || !title.trim() || !description.trim()) return;

    setIsSaving(true);
    try {
      await api.post("/protocols", {
        title: title.trim(),
        description: description.trim(),
        priority,
        conversationId,
        ...(assignedToSystemUserId ? { assignedToSystemUserId } : {}),
      });
      success("Protocolo registrado.");
      onCreated?.();
      onClose();
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      onClick={() => {
        if (!isSaving) onClose();
      }}
    >
      <div
        className="modal-card-lg space-y-5"
        role="dialog"
        aria-modal="true"
        aria-labelledby="conversation-protocol-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div>
          <h2
            id="conversation-protocol-modal-title"
            className="text-lg font-semibold text-slate-800"
          >
            Novo protocolo
          </h2>
          <p className="text-sm text-slate-500">
            O protocolo será vinculado a este atendimento.
          </p>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <label className="form-label block">
            Título
            <input
              className="form-input"
              required
              maxLength={200}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              autoFocus
            />
          </label>

          <label className="form-label block">
            Descrição
            <textarea
              className="form-input"
              required
              maxLength={10000}
              rows={5}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </label>

          <label className="form-label block">
            Prioridade
            <select
              className="form-input"
              value={priority}
              onChange={(event) =>
                setPriority(event.target.value as ProtocolPriority)
              }
            >
              {Object.entries(priorityLabels).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          <p className="text-sm text-slate-600">
            Solicitante obtido do atendimento de origem.
          </p>

          <PartySelect
            kind="system-user"
            label="Responsável (opcional)"
            value={assignedToSystemUserId}
            onChange={setAssignedToSystemUserId}
          />

          <div className="flex justify-end gap-3">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancelar
            </button>
            <LoadingButton
              type="submit"
              className="btn btn-neutral cursor-pointer"
              isLoading={isSaving}
              loadingLabel="Registrando"
              disabled={isSaving || !title.trim() || !description.trim()}
            >
              Registrar protocolo
            </LoadingButton>
          </div>
        </form>
      </div>
    </div>
  );
}
